import type { MemorialData, MediaItem, Offering } from '../hooks/useMemorialData';

export interface ExportData {
  memorial: MemorialData;
  media: MediaItem[];
  offerings: Offering[];
  tributes: { id: string; fromName: string; message: string | null; createdAt: number }[];
  articles: {
    id: string;
    title: string;
    excerpt: string | null;
    content: string;
    createdAt: number;
  }[];
  /** 馆内逝者；双人合葬会有 2 位（sortOrder 0 为主逝者） */
  people?: {
    id: string;
    name: string;
    birthDate: string | null;
    deathDate: string | null;
    portraitUrl: string | null;
  }[];
  theme: Record<string, string> | null;
  notary: {
    contentHash?: string;
    txHash?: string;
    blockNumber?: number;
    notarizedAt?: number;
    chain?: string;
  } | null;
}

const DEFAULT_CANDLE_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="64" height="64" fill="none" stroke="#6b4226" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="24" y="24" width="16" height="30" rx="2"/><path d="M32 24v-5"/><path d="M32 8c4 4 4 8 0 12-4-4-4-8 0-12z"/></svg>';

/** 导出页祭品图标：优先道具 SVG，否则回退统一蜡烛矢量（不使用 emoji） */
function svgFor(iconSvg?: string | null): string {
  if (iconSvg && iconSvg.trim().toLowerCase().startsWith('<svg')) return iconSvg;
  return DEFAULT_CANDLE_SVG;
}

/** HTML 转义，防止留言中的恶意内容破坏导出页面 */
function esc(input: string | null | undefined): string {
  if (!input) return '';
  return String(input)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function fmtDate(ts: number | null | undefined): string {
  if (!ts) return '';
  return new Date(ts * 1000).toLocaleString('zh-CN');
}

const DEFAULT_VARS: Record<string, string> = {
  '--c-bg': '#faf9f7',
  '--c-surface': '#ffffff',
  '--c-text': '#1c1917',
  '--c-muted': '#867c68',
  '--c-accent': '#d97706',
  '--c-border': '#e3dfd7',
};

/**
 * 生成完整的静态纪念册页面。
 *
 * 设计要点：
 *  - 完全自包含：样式内嵌，图片走相对路径 ./images/
 *  - 离线可用：双击 index.html 即可打开，不依赖任何服务器
 *  - 保留主题配色：导出后长什么样，和线上一致
 */
export function buildMemorialHtml(data: ExportData): string {
  const { memorial, media, offerings, tributes, articles, people = [], theme, notary } = data;
  const vars = { ...DEFAULT_VARS, ...(theme ?? {}) };

  const varLines = Object.entries(vars)
    .map(([k, v]) => `      ${k}: ${v};`)
    .join('\n');

  const photos = media.filter((m) => m.type === 'photo');
  const videos = media.filter((m) => m.type === 'video');

  const galleryHtml =
    photos.length > 0
      ? photos
          .map(
            (m, i) =>
              `      <figure class="media">
        <img src="./images/photo-${i + 1}.jpg" alt="${esc(m.caption ?? '')}" loading="lazy" />
        ${m.caption ? `<figcaption>${esc(m.caption)}</figcaption>` : ''}
      </figure>`
          )
          .join('\n')
      : '';

  const videoHtml =
    videos.length > 0
      ? videos
          .map(
            (m, i) =>
              `      <figure class="media">
        <video src="./images/video-${i + 1}.mp4" controls preload="metadata" playsinline></video>
        ${m.caption ? `<figcaption>${esc(m.caption)}</figcaption>` : ''}
      </figure>`
          )
          .join('\n')
      : '';

  const offeringsHtml =
    offerings.length > 0
      ? offerings
          .map(
            (o) =>
              `      <div class="offering">
        <span class="glyph">${svgFor(o.product?.iconSvg)}</span>
        <span class="who">${esc(o.fromName)}</span>
        ${o.dedication ? `<span class="word">${esc(o.dedication)}</span>` : ''}
      </div>`
          )
          .join('\n')
      : '';

  const tributesHtml =
    tributes.length > 0
      ? tributes
          .map(
            (t) =>
              `      <div class="tribute">
        <p class="who">${esc(t.fromName)}</p>
        ${t.message ? `<p class="word">${esc(t.message)}</p>` : ''}
        <p class="time">${fmtDate(t.createdAt)}</p>
      </div>`
          )
          .join('\n')
      : '';

  // 合葬：两位逝者并排，各自带遗像与生卒（单人馆不输出，沿用上方生卒行）
  const peopleHtml =
    people.length > 0
      ? people
          .map(
            (p) =>
              `        <div class="person">
          <div class="avatar">
            ${
              p.portraitUrl
                ? `<img src="${esc(p.portraitUrl)}" alt="${esc(p.name)}" />`
                : '<span class="ph">悼</span>'
            }
          </div>
          <p class="pname">${esc(p.name)}</p>
          <p class="pdate">${esc(p.birthDate ?? '—')} — ${esc(p.deathDate ?? '—')}</p>
        </div>`
          )
          .join('\n')
      : '';

  const articlesHtml =
    articles.length > 0
      ? articles
          .map(
            (a) =>
              `      <div class="article">
        <h3>${esc(a.title)}</h3>
        <p class="time">${fmtDate(a.createdAt)}</p>
        <div class="body">${esc(a.content)}</div>
      </div>`
          )
          .join('\n')
      : '';

  const notaryHtml = notary?.txHash
    ? `    <section class="block">
      <h2>链上存证</h2>
      <p class="note">本纪念馆的内容指纹已记录在区块链上，任何机构都无法单独修改或删除。</p>
      <dl class="kv">
        <dt>内容指纹</dt><dd class="mono">${esc(notary.contentHash ?? '')}</dd>
        <dt>交易哈希</dt><dd class="mono">${esc(notary.txHash)}</dd>
        ${notary.blockNumber ? `<dt>区块高度</dt><dd class="mono">#${notary.blockNumber}</dd>` : ''}
        ${notary.notarizedAt ? `<dt>存证时间</dt><dd>${fmtDate(notary.notarizedAt)}</dd>` : ''}
        ${notary.chain ? `<dt>所在网络</dt><dd>${esc(notary.chain)}</dd>` : ''}
      </dl>
    </section>`
    : '';

  return `<!doctype html>
<html lang="zh-CN">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<title>${esc(memorial.deceasedName)} · 纪念馆</title>
<style>
  :root {
${varLines}
  }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    padding: 0 20px 80px;
    background: var(--c-bg);
    color: var(--c-text);
    font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Hiragino Sans GB",
                 "Microsoft YaHei", serif;
    line-height: 1.8;
  }
  .wrap { max-width: 720px; margin: 0 auto; }
  header { text-align: center; padding: 64px 0 40px; }
  .eyebrow {
    font-size: 12px; letter-spacing: .3em; color: var(--c-muted); margin: 0;
  }
  h1 {
    font-size: 34px; font-weight: 400; margin: 18px 0 0; letter-spacing: .05em;
  }
  .en { font-style: italic; color: var(--c-muted); font-size: 14px; margin: 6px 0 0; }
  .span { color: var(--c-muted); font-size: 14px; margin: 14px 0 0; }
  .epitaph {
    margin: 32px auto 0; max-width: 520px; font-size: 18px; text-align: center;
  }
  .bio {
    margin: 28px auto 0; max-width: 560px; color: var(--c-muted);
    white-space: pre-wrap; font-size: 15px;
  }
  /* 合葬：两位逝者并排 */
  .people { display: flex; flex-wrap: wrap; justify-content: center; gap: 18px; margin-top: 22px; }
  .person { width: 148px; text-align: center; }
  .person .avatar {
    width: 84px; height: 84px; margin: 0 auto; border-radius: 50%; overflow: hidden;
    border: 1px solid var(--c-border); background: var(--c-surface);
    display: flex; align-items: center; justify-content: center;
  }
  .person .avatar img { width: 100%; height: 100%; object-fit: cover; }
  .person .avatar .ph { font-size: 26px; color: var(--c-muted); }
  .person .pname { font-size: 15px; margin: 8px 0 0; }
  .person .pdate { font-size: 11px; color: var(--c-muted); margin: 4px 0 0; }
  .block { margin-top: 56px; }
  h2 {
    font-size: 12px; letter-spacing: .25em; color: var(--c-muted);
    font-weight: 400; text-align: center; margin-bottom: 28px;
  }
  .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 14px; }
  .media { margin: 0; }
  .media img, .media video {
    width: 100%; aspect-ratio: 1; object-fit: cover; border-radius: 12px;
    border: 1px solid var(--c-border); display: block; background: var(--c-surface);
  }
  figcaption { font-size: 12px; color: var(--c-muted); margin-top: 6px; text-align: center; }
  .offerings, .tributes {
    display: grid; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); gap: 12px;
  }
  .offering, .tribute {
    border: 1px solid var(--c-border); border-radius: 12px; padding: 14px;
    background: var(--c-surface);
  }
  .offering .glyph { display: block; margin-bottom: 6px; }
  .offering .glyph svg { width: 28px; height: 28px; }
  .offering .who, .tribute .who { font-size: 13px; margin: 0; }
  .offering .word, .tribute .word { font-size: 13px; color: var(--c-muted); margin: 6px 0 0; }
  .tribute .time { font-size: 11px; color: var(--c-muted); margin: 8px 0 0; }
  .articles { display: block; }
  .article {
    border: 1px solid var(--c-border); border-radius: 12px; padding: 18px 20px;
    background: var(--c-surface);
  }
  .article + .article { margin-top: 14px; }
  .article h3 { font-size: 17px; font-weight: 400; margin: 0; letter-spacing: .02em; }
  .article .time { font-size: 11px; color: var(--c-muted); margin: 6px 0 0; }
  .article .body {
    font-size: 14px; margin: 12px 0 0; white-space: pre-wrap; color: var(--c-text);
  }
  .note { font-size: 13px; color: var(--c-muted); }
  .kv { margin: 0; font-size: 13px; }
  .kv dt { color: var(--c-muted); margin-top: 10px; }
  .kv dd { margin: 2px 0 0; word-break: break-all; }
  .mono { font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 12px; }
  footer {
    margin-top: 72px; text-align: center; font-size: 12px; color: var(--c-muted);
  }
  .empty { text-align: center; color: var(--c-muted); font-size: 14px; }
</style>
</head>
<body>
  <div class="wrap">
    <header>
      <p class="eyebrow">IN LOVING MEMORY</p>
      <h1>${esc(memorial.deceasedName)}</h1>
      ${memorial.deceasedNameEn ? `<p class="en">${esc(memorial.deceasedNameEn)}</p>` : ''}
      <p class="span">${esc(memorial.birthDate ?? '—')} — ${esc(memorial.deathDate ?? '—')}</p>
      ${
        people.length > 1
          ? `<div class="people">
${peopleHtml}
      </div>`
          : ''
      }
      ${memorial.epitaph ? `<p class="epitaph">「${esc(memorial.epitaph)}」</p>` : ''}
      ${memorial.biography ? `<p class="bio">${esc(memorial.biography)}</p>` : ''}
    </header>

    ${
      galleryHtml || videoHtml
        ? `<section class="block">
      <h2>相 册</h2>
      <div class="grid">
${galleryHtml}
${videoHtml}
      </div>
    </section>`
        : ''
    }

    ${
      offeringsHtml
        ? `<section class="block">
      <h2>祭 台</h2>
      <div class="offerings">
${offeringsHtml}
      </div>
    </section>`
        : ''
    }

    ${
      tributesHtml
        ? `<section class="block">
      <h2>追 思</h2>
      <div class="tributes">
${tributesHtml}
      </div>
    </section>`
        : ''
    }

    ${
      articlesHtml
        ? `<section class="block">
      <h2>文 章</h2>
      <div class="articles">
${articlesHtml}
      </div>
    </section>`
        : ''
    }

${notaryHtml}

    <footer>
      <p>本页由「线上墓迪」导出 · 共 ${memorial.tributeCount} 次祭奠</p>
      <p>导出时间：${new Date().toLocaleString('zh-CN')}</p>
      <p>此文件完全自包含，可离线打开，也可自行上传至任何地方长久保存</p>
    </footer>
  </div>
</body>
</html>`;
}
